import React, { useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { AccountInfo, BrowserAuthError, InteractionRequiredAuthError } from '@azure/msal-browser';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { loginRequest } from '../constants/authConfig';
import { AppDispatch } from 'app/store';
import { LoginWithSSO } from 'features/Core/Authentification/state/authentificationSlice';
import { ZeitonAccessToken } from 'shared/utils/common';

const defaulthError = 'The app is trying to access a service (CentralServices-WebShopBackend)that your organization lacks a service principal for. Contact your IT Admin to review the configuration of your service subscriptions or consent to the application in order to create the required Service Principal.'

const SignInButton: React.FC = () => {
    const { t } = useTranslation();
    const { instance } = useMsal();
    const dispatch = useDispatch<AppDispatch>();
    const [ssoError, setSsoError] = useState<string>('');

    // Acquire the access token for the signed-in account, store it, and let the
    // auth slice exchange it with the Zeiton backend. Falls back to an
    // interactive popup when a silent token can no longer be issued.
    const acquireTokenAndLogin = async (account: AccountInfo) => {
        const request = { ...loginRequest, account };
        let accessToken: string | undefined;

        try {
            const response = await instance.acquireTokenSilent(request);
            accessToken = response.accessToken;
        } catch (error) {
            if (error instanceof InteractionRequiredAuthError) {
                const response = await instance.acquireTokenPopup(request);
                accessToken = response.accessToken;
            } else {
                throw error;
            }
        }

        if (!accessToken) {
            setSsoError(defaulthError);
            return;
        }

        localStorage.setItem(ZeitonAccessToken, accessToken);
        dispatch(LoginWithSSO());
    };

    const handleLogin = async () => {
        setSsoError('');
        try {
            const result = await instance.loginPopup(loginRequest);
            const account = result.account ?? instance.getAllAccounts()[0];
            if (!account) {
                setSsoError(defaulthError);
                return;
            }
            instance.setActiveAccount(account);
            await acquireTokenAndLogin(account);
        } catch (error) {
            // The user closing the popup is not an error worth surfacing.
            if (error instanceof BrowserAuthError && error.errorCode === 'user_cancelled') {
                return;
            }
            console.error(error);
            setSsoError(t('Microsoft login failed. Please try again.')!);
        }
    };

    return (
        <>
            <div className="sso-login" id="sso-login-btn" onClick={handleLogin}>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M2 2H11.4737V11.4737H2V2ZM11.4737 22H2V12.5263H11.4737V22ZM22 2V11.4737H12.5263V2H22ZM22 22H12.5263V12.5263H22V22Z" fill="var(--s-brand-text)" />
                </svg>
                <p className="sso-login-link">{t("Log in with Microsoft")}</p>
            </div>
            {ssoError && <p className="error-visible">{ssoError}</p>}
        </>
    );
};

export default SignInButton;
